import { createRoutesFromElements, Navigate, Outlet, Route } from 'react-router';
import { Tooltip } from 'react-tooltip';
import { ProtectedRoute } from 'softcat:components/protected-route.tsx';
import { UserRole } from 'softcat:config/user-role.ts';
import { AuthProvider } from 'softcat:contexts/auth.tsx';
import { Apps } from 'softcat:routes/apps.tsx';
import { AppsCreate } from 'softcat:routes/apps-create.tsx';
import { AppsEdit } from 'softcat:routes/apps-edit.tsx';
import { AuthCallback } from 'softcat:routes/auth-callback.tsx';
import { Catalogues } from 'softcat:routes/catalogues.tsx';
import { CataloguesCreate } from 'softcat:routes/catalogues-create.tsx';
import { CataloguesEdit } from 'softcat:routes/catalogues-edit.tsx';
import { Certificates } from 'softcat:routes/certificates.tsx';
import { CertificatesCreate } from 'softcat:routes/certificates-create.tsx';
import { CertificatesEdit } from 'softcat:routes/certificates-edit.tsx';
import { ConfigConfigVersions } from 'softcat:routes/config-config-versions.tsx';
import { ConfigEntitlements } from 'softcat:routes/config-entitlements.tsx';
import { ConfigPlatforms } from 'softcat:routes/config-platforms.tsx';
import { Locations } from 'softcat:routes/locations.tsx';
import { LocationsCreate } from 'softcat:routes/locations-create.tsx';
import { LocationsEdit } from 'softcat:routes/locations-edit.tsx';
import { NotFound } from 'softcat:routes/not-found.tsx';
import { Root } from 'softcat:routes/root.tsx';
import { SignIn } from 'softcat:routes/sign-in.tsx';
import { Users } from 'softcat:routes/users.tsx';
import { UsersEdit } from 'softcat:routes/users-edit.tsx';
import { EnvironmentUtils } from 'softcat:utils/environment.ts';
import { MonitoringUtils } from 'softcat:utils/monitoring.ts';
import { SsoConfig } from 'softcat:config/sso.ts';

const { host } = globalThis.location;

export const Routes = (mode: string) => {
    MonitoringUtils.initialiseGoogleAnalytics();
    MonitoringUtils.initialiseSentry(mode, EnvironmentUtils.getName(host));

    return createRoutesFromElements(
        <Route
            element={
                <AuthProvider contextDefaults={{ useCognitoLogin: SsoConfig.USE_COGNITO_LOGIN }}>
                    <Outlet />
                    <Tooltip className={'!p-2 !text-xs'} delayShow={100} id="app-tooltip" />
                </AuthProvider>
            }
        >
            <Route element={<AuthCallback />} path="auth/callback" />
            <Route element={<SignIn />} path="login" />
            <Route element={<Root />} path="">
                <Route element={<Navigate replace to="apps" />} path="" />
                <Route element={<ProtectedRoute role={UserRole.USER} />} path="apps">
                    <Route element={<Apps />} path="" />
                    <Route element={<AppsCreate />} path="create" />
                    <Route element={<AppsEdit />} path="edit/:id/:version" />
                </Route>
                <Route element={<ProtectedRoute role={UserRole.USER} />} path="catalogues">
                    <Route element={<Catalogues />} path="" />
                    <Route element={<CataloguesCreate />} path="create" />
                    <Route element={<CataloguesEdit />} path="edit/:name/:version" />
                </Route>
                <Route element={<ProtectedRoute role={UserRole.SUPERADMIN} />} path="certificates">
                    <Route element={<Certificates />} path="" />
                    <Route element={<CertificatesCreate />} path="create" />
                    <Route element={<CertificatesEdit />} path="edit/:id" />
                </Route>
                <Route element={<ProtectedRoute role={UserRole.ADMIN} />} path="locations">
                    <Route element={<Locations />} path="" />
                    <Route element={<LocationsCreate />} path="create" />
                    <Route element={<LocationsEdit />} path="edit/:name" />
                </Route>
                <Route element={<ProtectedRoute role={UserRole.SUPERADMIN} />} path="users">
                    <Route element={<Users />} path="" />
                    <Route element={<UsersEdit />} path="edit/:id" />
                </Route>
                <Route element={<Navigate replace to="entitlements" />} path="config" />
                <Route element={<ProtectedRoute role={UserRole.SUPERADMIN} />} path="config">
                    <Route
                        element={<ConfigConfigVersions disabled={true} />}
                        path="config-versions"
                    />
                    <Route element={<ConfigEntitlements />} path="entitlements" />
                    <Route element={<ConfigPlatforms />} path="platforms" />
                </Route>
            </Route>
            <Route element={<NotFound />} path="*" />
        </Route>,
    );
};
